import React, { useEffect, useState } from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils';
import { Table } from 'react-bootstrap';
import { DisplayImage } from '../../widgets/components/General/DisplayImage';

declare module '@tanstack/react-table' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>;
    }
    interface FilterMeta {
        itemRank: RankingInfo;
    }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
};

interface PerformaStakeholder {
    id: number;
    name: string;
    designation: string;
    security_class: string[];
    fully_diluted_shares: number;
    amount_invested: number;
    projected_payout: number;
    email: string;
    phone: string;
}

export const DataTableServerSideWaterfallStakeholder = () => {
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [data, setData] = useState<PerformaStakeholder[]>([]);

    const columns = React.useMemo<ColumnDef<PerformaStakeholder, any>[]>(() => [
        {
            accessorKey: 'name',
            id: 'name',
            header: 'Shareholdings',
            cell: (info) => (
                <div className="d-flex align-items-center gap-2">
                    <DisplayImage imgName={''} alt={'profile-pic'} height={40} width={40} />
                    <div>
                        <h5 className="m-0 font-size-13">{info.row.original.name}</h5>
                        <p className="m-0">{info.row.original.designation}</p>
                    </div>
                </div>
            ),
            filterFn: 'includesString',
        },
        {
            accessorKey: 'security_class',
            id: 'security_class',
            header: 'Security Classes',
            cell: (info) => {
                const securityClasses = info.getValue();
                if (Array.isArray(securityClasses)) {
                    return (
                        <div className="d-flex gap-1">
                            {securityClasses.map((securityClass, index) => (
                                <span key={index} className={`badge badge-${getBadgeColor(securityClass)}`}>
                                    {securityClass}
                                </span>
                            ))}
                        </div>
                    );
                } else {
                    return <span>No security classes</span>; // Fallback for non-array values
                }
            },
            filterFn: 'includesString',
        }
        ,
        {
            accessorKey: 'fully_diluted_shares',
            id: 'fully_diluted_shares',
            header: 'Fully Diluted Shares',
            cell: (info) => info.getValue().toLocaleString(),
            filterFn: 'includesString',
        },
        {
            accessorKey: 'amount_invested',
            id: 'amount_invested',
            header: 'Amount Invested',
            cell: (info) => `$${info.getValue().toLocaleString()}`,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'projected_payout',
            id: 'projected_payout',
            header: 'Projected Payout',
            cell: (info) => `$${info.getValue().toLocaleString()}`,
            filterFn: 'includesString',
        },
    ], []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch('https://dummyjson.com/c/76a9-5adc-45b7-8b77');
                const result = await response.json();
                console.log(result, 'Dummy data from API');
                setData(result);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            columnFilters,
            globalFilter,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: 'fuzzy',
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <>
            <div className="bg-white rounded-bottom p-4" style={{ overflowX: 'auto' }}>
                <Table className="w-100" style={{ minWidth: '600px' }}>
                    <thead className="custom-thead border-bottom">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr
                                key={headerGroup.id}
                                className="font-size-12 font-weight-400 table-row border-dark-mode"
                            >
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} colSpan={header.colSpan} className="min-w-120px pb-6 pt-4">
                                        {header.isPlaceholder ? null : (
                                            <div
                                                className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {{
                                                    asc: ' ▲',
                                                    desc: ' ▼',
                                                }[header.column.getIsSorted() as string] ?? null}
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row, index) => (
                            <tr key={row.id} className="border-bottom">
                                {row.getVisibleCells().map((cell, index) => (
                                    <td key={cell.id} className="pt-5 border-0">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </>
    );
};

const getBadgeColor = (securityClass: string) => {
    switch (securityClass) {
        case 'CA':
            return 'success';
        case 'SF1':
            return 'warning';
        case 'SB':
            return 'primary';
        default:
            return 'secondary';
    }
};
